/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { MetricClass } from '@squiz/dxp-metric-collection-types';
import {
  Breadcrumbs,
  BreadcrumbsProps,
  DashboardSummary,
  DashboardSummaryItem,
  DashboardSummaryItemProps,
  ICON_CAPABILITY_CUSTOMER_DATA_PLATFORM,
  Spinner,
} from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { DxpScreenBanner } from 'apps/console-core/src/Components/DxpScreenBanner/DxpScreenBanner';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import { LogLevel, LoggingService, getLoggingService } from 'apps/console-core/src/Services/logging';
import {
  EntityMetricSeries,
  EntityMetricSeriesItem,
  MetricCollectionService,
  MetricCollectionServiceGetMetricSeriesDto,
  PeriodSize,
  getMetricCollectionService,
} from 'apps/console-core/src/Services/metric-collection';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { METRIC_CDP_EVENTS, METRIC_CDP_SCV_CUSTOMERS, METRIC_CDP_SEGMENTS } from '../../Constants/Metric.constants';
import { CDP_STRINGS } from '../../Constants/Strings.constants';
import { DashboardProps } from './Dashboard.props';

/**
 * Get a date range in iso format
 * @param date The date to start from (usually now).
 * @param fromDays The relative amount of days from.
 * @param toDays The relative amount of days to.
 * @returns Returns a date range in ISO format.
 */
export const getDateRange = (date: Date, fromDays = 0, toDays = 0): Array<string> => {
  const dateFrom = new Date(date.getTime());
  const dateTo = new Date(date.getTime());
  dateFrom.setDate(dateFrom.getDate() + fromDays);
  dateTo.setDate(dateTo.getDate() + toDays);
  return [dateFrom.toISOString(), dateTo.toISOString()];
};

/**
 * Get the value from MCS data by the latest date.
 * @param data The MCS data result.
 * @param defaultValue The default value used if no value can be found (e.g. empty result set).
 * @returns The latest value.
 */
export const getValueByLatestDateFromMCSData = (data: EntityMetricSeries['data'], defaultValue = 0): number => {
  const latest = data.reduce((acc: null | EntityMetricSeriesItem, curr) => {
    if (acc === null || new Date(acc.timestamp) < new Date(curr.timestamp)) {
      acc = curr;
    }
    return acc;
  }, null);
  if (latest !== null) {
    return latest.value;
  }
  return defaultValue;
};

/**
 * Get current and previous MCS data.
 * @param mcs The MCS service.
 * @param metricid The metric id to search.
 * @param days The number of days to search back from.
 * @param maybeMetricDto Any additional dto properties.
 * @returns Values in the format of [current, previous].
 */
export const getMCSSummaryData = async (
  mcs: MetricCollectionService,
  metricid: string,
  days: number = 0,
  maybeMetricDto: Partial<MetricCollectionServiceGetMetricSeriesDto> = {},
): Promise<Array<number>> => {
  try {
    const now = new Date();
    const currentDaysAgo = getDateRange(now, days, 0);
    const previousDaysAgo = getDateRange(now, days * 2, days);

    const currentMetricDataDto = {
      dateFrom: currentDaysAgo[0],
      dateTo: currentDaysAgo[1],
      metricClass: MetricClass.custom,
      metricId: metricid,
      periodSize: PeriodSize.day,
      ...maybeMetricDto,
    };
    const currentMetricData = mcs.getMetricSeries(currentMetricDataDto);

    const previousMetricDataDto = {
      dateFrom: previousDaysAgo[0],
      dateTo: previousDaysAgo[1],
      metricClass: MetricClass.custom,
      metricId: metricid,
      periodSize: PeriodSize.day,
      ...maybeMetricDto,
    };
    const previousMetricData = mcs.getMetricSeries(previousMetricDataDto);

    return Promise.all([lastValueFrom(currentMetricData), lastValueFrom(previousMetricData)])
      .then((results) => {
        if (!results) {
          return [0, 0];
        }
        return [
          getValueByLatestDateFromMCSData((results[0] as EntityMetricSeries)?.data),
          getValueByLatestDateFromMCSData((results[1] as EntityMetricSeries)?.data),
        ] as Array<number>;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error({ error });
        return [0, 0];
      });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error({ error });
    return [0, 0];
  }
};

/**
 * Get the summary item props for events.
 * @param mcs The metric collection service.
 */
export const getEventCountSummary = async (
  mcs: MetricCollectionService,
): Promise<Partial<DashboardSummaryItemProps>> => {
  const result = await getMCSSummaryData(mcs, METRIC_CDP_EVENTS, -30);

  return {
    count: result[0],
    previousCount: result[1],
    timePeriod: `days`,
    timeValue: 30,
  };
};

/**
 * Get the summary item props for errors.
 * @param logging The logging service.
 */
export const getErrorCountSummary = async (logging: LoggingService): Promise<Partial<DashboardSummaryItemProps>> => {
  const now = new Date();
  const days = -30;
  const currentDaysAgo = getDateRange(now, days, 0);
  const previousDaysAgo = getDateRange(now, days * 2, days);
  const currentErrorLogs = await lastValueFrom(
    logging.getLogs({
      filterFrom: currentDaysAgo[0],
      filterLevels: [LogLevel.ERROR],
      filterTag: [`capability:cdp`],
      filterTo: currentDaysAgo[1],
    }),
  );
  const previousErrorLogs = await lastValueFrom(
    logging.getLogs({
      filterFrom: previousDaysAgo[0],
      filterLevels: [LogLevel.ERROR],
      filterTag: [`capability:cdp`],
      filterTo: previousDaysAgo[1],
    }),
  );
  return {
    count: Object.keys(currentErrorLogs?.data || {}).length,
    inverted: true,
    previousCount: Object.keys(previousErrorLogs?.data || {}).length,
    timePeriod: `days`,
    timeValue: 7,
  };
};

/**
 * Get the summary item props for the number of customers.
 * @param mcs The metric collection service.
 */
export const getCustomerCountSummary = async (
  mcs: MetricCollectionService,
): Promise<Partial<DashboardSummaryItemProps>> => {
  const result = await getMCSSummaryData(mcs, METRIC_CDP_SCV_CUSTOMERS, -30);
  return {
    count: result[0],
    previousCount: result[1],
    timePeriod: `days`,
    timeValue: 30,
  };
};

/**
 * Get the summary item props for the number of segments.
 * @param {MetricCollectionService} mcs The metric collection service.
 * @returns {Promise<Partial<DashboardSummaryItemProps>>} `<DashboardSummaryItem />` props.
 */
export const getSegmentCountSummary = async (
  mcs: MetricCollectionService,
): Promise<Partial<DashboardSummaryItemProps>> => {
  const result = await getMCSSummaryData(mcs, METRIC_CDP_SEGMENTS, -30);
  return {
    count: result[0],
    previousCount: result[1],
    timePeriod: `days`,
    timeValue: 30,
  };
};

/**
 * The CDP Dashboard.
 * @param {DashboardProps} props Props for `<Dashboard />`.
 * @export
 * @returns {JSX.Element} The `<Dashboard />` component.
 */
export const Dashboard = (props: DashboardProps): JSX.Element => {
  const { t } = useTranslation();
  const metricCollectionService = getMetricCollectionService();
  const loggingService = getLoggingService();
  const [summary, setSummary] = useState<Array<DashboardSummaryItemProps>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Labels
  const labels = useMemo(() => {
    return {
      dashboardCustomers: t(CDP_STRINGS.dashboardCustomers),
      dashboardErrors: t(CDP_STRINGS.dashboardErrors),
      dashboardEvents: t(CDP_STRINGS.dashboardEvents),
      dashboardSegments: t(CDP_STRINGS.dashboardSegments),
      dashboardTotalCustomers: t(CDP_STRINGS.dashboardTotalCustomers),
      dashboardTotalSegments: t(CDP_STRINGS.dashboardTotalSegments),
      heading: t(CDP_STRINGS.dashboard),
    };
  }, [t]);

  // Breadcrumbs
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const breadcrumbProps: BreadcrumbsProps = {
    currentPageName: labels.heading,
    links: [capabilityBreadcrumbs[0]],
  };
  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="cdp-breadcrumbs" />;

  // Metric data.
  useEffect((): void => {
    Promise.all([
      getEventCountSummary(metricCollectionService),
      getErrorCountSummary(loggingService),
      getCustomerCountSummary(metricCollectionService),
      getSegmentCountSummary(metricCollectionService),
    ])
      .then((summaries) => {
        summaries[0] = { ...summaries[0], title: labels.dashboardEvents } as DashboardSummaryItemProps;
        summaries[1] = { ...summaries[1], title: labels.dashboardErrors } as DashboardSummaryItemProps;
        summaries[2] = {
          ...summaries[2],
          description: labels.dashboardTotalCustomers,
          title: labels.dashboardCustomers,
        } as DashboardSummaryItemProps;
        summaries[3] = {
          ...summaries[3],
          description: labels.dashboardTotalSegments,
          title: labels.dashboardSegments,
        } as DashboardSummaryItemProps;
        setIsLoading(false);
        return setSummary(summaries as Array<DashboardSummaryItemProps>);
      })
      .catch((error) => {
        setIsLoading(false);
        // eslint-disable-next-line no-console
        console.error(error);
      });
  }, [
    labels.dashboardCustomers,
    labels.dashboardErrors,
    labels.dashboardEvents,
    labels.dashboardSegments,
    labels.dashboardTotalCustomers,
    labels.dashboardTotalSegments,
    loggingService,
    metricCollectionService,
  ]);

  const screenHeading = labels.heading;
  let screenContent = (
    <>
      <DashboardSummary>
        {summary.map((summaryProps): JSX.Element => {
          return <DashboardSummaryItem {...summaryProps} key={summaryProps.title} />;
        })}
      </DashboardSummary>
    </>
  );

  if (isLoading) {
    screenContent = (
      <div className="dashboard--loading">
        <Spinner />
      </div>
    );
  }

  return (
    <DxpScreen breadcrumbs={breadcrumbs} heading={screenHeading} hideHeading>
      <DxpScreenBanner
        description="Manage your customer data"
        icon={ICON_CAPABILITY_CUSTOMER_DATA_PLATFORM}
        title="Customer data platform"
      />
      {screenContent}
    </DxpScreen>
  );
};
